import { Badge, Group, MultiSelect, Select, Stack, Table, Text, TextInput } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useDebouncedValue } from '@mantine/hooks';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useGetPageReportPages } from '../api/getPages';
import { useGetPageReports } from '../api/getReports';
import { aiReportTypes } from '../types';
import {
  GEO_LABEL,
  KIND_LABEL,
  SOURCE_LABEL,
  STATUS_LABEL,
  formatCost,
  formatDateTime,
  formatUtcDate,
  toIsoDay,
} from '../utils/reportDocument';

import { ListLoader, ServerError } from '@/components';
import { useSearchState } from '@/hooks/useSearchState';

function isoToLocalDate(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? null : date;
}

const STATUS_COLOR: Record<aiReportTypes.Status, string> = {
  queued: 'gray',
  running: 'blue',
  done: 'teal',
  empty: 'yellow',
  failed: 'red',
};

export const ReportsTable = () => {
  const navigate = useNavigate();
  const [geo, setGeo] = useSearchState('geo', '');
  const [source, setSource] = useSearchState('source', '');
  const [status, setStatus] = useSearchState('status', '');
  const [language, setLanguage] = useSearchState('language', '');
  const [author, setAuthor] = useSearchState('author', '');
  const [searchParams, setSearchParams] = useSearchParams();
  const periodFrom = searchParams.get('periodFrom') ?? '';
  const periodTo = searchParams.get('periodTo') ?? '';
  const [authorQuery] = useDebouncedValue(author, 400);
  const pageIdsParam = searchParams.get('pageIds') ?? '';
  const pageIds = useMemo(
    () => pageIdsParam.split(',').filter((id) => id.length > 0),
    [pageIdsParam]
  );
  const period: [Date | null, Date | null] = [isoToLocalDate(periodFrom), isoToLocalDate(periodTo)];

  const filter: aiReportTypes.ListFilter = {
    geo: geo || undefined,
    source: source || undefined,
    status: status || undefined,
    language: language || undefined,
    pageIds: pageIds.length ? pageIds : undefined,
    author: authorQuery.trim() || undefined,
    periodFrom: period[0] ? toIsoDay(period[0]) : undefined,
    periodTo: period[1] ? toIsoDay(period[1]) : undefined,
  };

  const reportsQuery = useGetPageReports({ filter });
  const reports = useMemo(() => reportsQuery.data?.reports ?? [], [reportsQuery.data?.reports]);

  // Options: pages endpoint when geo + source are chosen, plus pages seen in loaded reports.
  const pagesQuery = useGetPageReportPages({
    geo: (geo || null) as aiReportTypes.Geo | null,
    source: (source || null) as aiReportTypes.Source | null,
  });
  const [knownPages, setKnownPages] = useState<Record<string, string>>({});
  useEffect(() => {
    const found: Record<string, string> = {};
    for (const option of pagesQuery.data?.pages ?? []) found[option._id] = option.name;
    for (const report of reports) {
      if (report.type === 'page' && report.pageId) found[report.pageId] = report.pageName;
    }
    setKnownPages((prev) => {
      const next = { ...prev, ...found };
      const changed =
        Object.keys(next).length !== Object.keys(prev).length ||
        Object.keys(found).some((id) => prev[id] !== found[id]);
      return changed ? next : prev;
    });
  }, [pagesQuery.data, reports]);
  const pageOptions = useMemo(() => {
    const ids = new Set([...Object.keys(knownPages), ...pageIds]);
    return [...ids]
      .map((id) => ({ value: id, label: knownPages[id] ?? id }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [knownPages, pageIds]);
  const isLive = reports.some(
    (report) => report.status === 'queued' || report.status === 'running'
  );

  return (
    <Stack gap="sm">
      <Group align="flex-end" grow>
        <Select
          label="Гео"
          clearable
          data={[
            { value: 'latam', label: 'Лат.Ам' },
            { value: 'cis', label: 'СНГ' },
          ]}
          value={geo || null}
          onChange={(value) => setGeo(value ?? '')}
        />
        <Select
          label="Источник"
          clearable
          data={[
            { value: 'telegram', label: 'Telegram' },
            { value: 'instagram', label: 'Instagram' },
            { value: 'facebook', label: 'Facebook' },
          ]}
          value={source || null}
          onChange={(value) => setSource(value ?? '')}
        />
        <Select
          label="Статус"
          clearable
          data={Object.entries(STATUS_LABEL).map(([value, label]) => ({ value, label }))}
          value={status || null}
          onChange={(value) => setStatus(value ?? '')}
        />
        <Select
          label="Язык"
          clearable
          data={[
            { value: 'ru', label: 'RU' },
            { value: 'es', label: 'ES' },
          ]}
          value={language || null}
          onChange={(value) => setLanguage(value ?? '')}
        />
        <MultiSelect
          label="Страница"
          placeholder={pageIds.length ? undefined : 'Все страницы'}
          searchable
          clearable
          data={pageOptions}
          value={pageIds}
          nothingFoundMessage="Страницы не найдены"
          onChange={(value) => {
            const next = new URLSearchParams(searchParams);
            if (value.length) next.set('pageIds', value.join(','));
            else next.delete('pageIds');
            setSearchParams(next, { replace: true });
          }}
        />
        <TextInput
          label="Автор"
          value={author}
          onChange={(event) => setAuthor(event.currentTarget.value)}
        />
        <DatePickerInput
          type="range"
          label="Период отчёта"
          value={period}
          valueFormat="DD.MM.YYYY"
          clearable
          onChange={([from, to]) => {
            const next = new URLSearchParams(searchParams);
            if (from) next.set('periodFrom', toIsoDay(from));
            else next.delete('periodFrom');
            if (to) next.set('periodTo', toIsoDay(to));
            else next.delete('periodTo');
            setSearchParams(next, { replace: true });
          }}
        />
      </Group>
      {isLive && (
        <Text size="sm" c="dimmed">
          Статус обновляется без перезагрузки страницы.
        </Text>
      )}
      {reportsQuery.isLoading && <ListLoader height="240px" />}
      {reportsQuery.isError && (
        <ServerError onRetry={() => reportsQuery.refetch()} isFetching={reportsQuery.isFetching} />
      )}
      {!reportsQuery.isLoading && !reportsQuery.isError && reports.length === 0 && (
        <Text>Отчётов пока нет.</Text>
      )}
      {reports.length > 0 && (
        <Table highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Дата</Table.Th>
              <Table.Th>Автор</Table.Th>
              <Table.Th>Гео</Table.Th>
              <Table.Th>Источник</Table.Th>
              <Table.Th>Страница</Table.Th>
              <Table.Th>Период</Table.Th>
              <Table.Th>Тип</Table.Th>
              <Table.Th>Язык</Table.Th>
              <Table.Th>Статус</Table.Th>
              <Table.Th>Стоимость</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {reports.map((report) => (
              <Table.Tr
                key={report._id}
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/ai-reports/${report._id}`)}
              >
                <Table.Td>{formatDateTime(report.createdAt)}</Table.Td>
                <Table.Td>{report.createdByName}</Table.Td>
                <Table.Td>{GEO_LABEL[report.geo]}</Table.Td>
                <Table.Td>{SOURCE_LABEL[report.source]}</Table.Td>
                <Table.Td>{report.pageName}</Table.Td>
                <Table.Td>
                  {formatUtcDate(report.periodFrom)} — {formatUtcDate(report.periodTo)}
                </Table.Td>
                <Table.Td>{KIND_LABEL[report.type]}</Table.Td>
                <Table.Td>{report.language.toUpperCase()}</Table.Td>
                <Table.Td>
                  <Badge color={STATUS_COLOR[report.status]} variant="light">
                    {STATUS_LABEL[report.status]}
                  </Badge>
                </Table.Td>
                <Table.Td>{formatCost(report.costUsd)}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  );
};
